<template>
    <v-select
        v-if="productsLoaded"
        v-model="productToAdd"
        :options="products"
        :filterable="false"
        label="name"
        placeholder="Lägg till produkt"
        @update:modelValue="addProduct"
        @search="onSearch"
    >
        <template #search="{ attributes, events }">
            <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
                :required="required"
            />
        </template>
        <template #option="product">
            <div
                class="d-flex align-items-start"
                :style="`white-space: normal; margin: 0 -17px; ${
                    product.deleted || !product.in_stock ? 'opacity: 0.8;' : ''
                }`"
            >
                <template v-if="product.type === 'product'">
                    <img :src="product.display_image_url" alt="" class="product-image mr-2" />
                    <div class="flex-grow-1 d-flex align-items-start justify-content-between">
                        <div>
                            <strong>{{ product.name }}</strong><br />
                            {{ product.price_including_vat }}
                            {{
                                $t("products.units." + product.price_unit, {
                                    currency: formattedCurrency(product.currency),
                                })
                            }}
                            <div class="text-muted" style="font-size: 70%">
                                {{ product.sku }}
                            </div>
                            <template v-if="product.alternative_product">
                                <br />
                                <small>Alternativ: {{ product.alternative_product.name }}</small>
                            </template>
                        </div>
                        <i
                            v-if="product.deleted || !product.in_stock"
                            class="fa-regular fa-warning"
                            style="margin-top: 3px; margin-right: 2px"
                        ></i>
                    </div>
                </template>

                <template v-if="product.type === 'bundle'">
                    <div class="bundle-icon mr-2">
                        <i class="fa-light fa-cubes"></i>
                    </div>
                    <div class="flex-grow-1 d-flex align-items-start justify-content-between">
                        <div>
                            <strong>{{ product.name }}</strong><br />
                            <small>{{ product.products.length }} produkter</small>
                        </div>
                        <i
                            v-if="product.deleted || !product.in_stock"
                            class="fa-regular fa-warning"
                            style="margin-top: 3px; margin-right: 2px"
                        ></i>
                    </div>
                </template>
            </div>
        </template>
    </v-select>
    <div v-else class="row d-flex justify-content-center">
        <div class="spinner-border text-info" role="status">
            <span class="sr-only">Laddar...</span>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.product-image,
.bundle-icon {
    width: 20%;
    height: auto;

    aspect-ratio: 1 / 1;
}

.product-image {
    object-fit: contain;
    object-position: center top;
}

.bundle-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.5rem;
    line-height: 100%;
}
</style>

<script setup lang="ts">
import { onMounted, ref, toRefs, watch } from "vue";
import { debounce } from "lodash";
import { useI18n } from "../../composables/useI18n";
const DEFAULT_PRODUCTS_LIMIT = 500;
import { downloadProducts } from "./downloadProducts";
const props = defineProps({
  recurringPayments: {
    type: Object,
    default: null
  },
  ignoreOutOfStock: {
    type: Boolean,
    required: true
  },
  ignoreDeleted: {
    type: Boolean,
    required: true
  },
  allowSoloProducts: {
    type: Boolean,
    required: true
  },
  required: {
    type: Boolean,
    required: true
  },
  defaultDiscountType: {
    type: String,
    required: false,
    default: "kr"
  },
  quoteProfileId: {
    type: Number,
    required: false,
    default: null
  }
});
const {
  recurringPayments,
  ignoreOutOfStock,
  ignoreDeleted,
  allowSoloProducts,
  required,
  defaultDiscountType,
  quoteProfileId
} = toRefs(props);
const emit = defineEmits(["onAddProducts"]);
const {
  t
} = useI18n();
const productsQuery = ref("");
const productsLimit = ref(DEFAULT_PRODUCTS_LIMIT);
const productToAdd = ref(null);
const products = ref([]);
const productsLoaded = ref(false);
const getProducts = ref(undefined);
function addProduct(product) {
  if (!product) {
    return;
  }

  const products = [];
  if (product.type === "bundle") {
    product.products.forEach(prodRef => {
      products.push({
        product: prodRef.product,
        amount: prodRef.amount,
        is_add_on: false,
        promptedValue: "",
        discount_type: defaultDiscountType.value,
        discount: undefined,
        seller_assigned_price: product.minimum_price_including_vat
      });
    });
  }
  if (product.type === "product") {
    products.push({
      product: product,
      amount: 1,
      is_add_on: false,
      promptedValue: "",
      discount_type: defaultDiscountType.value,
      discount: undefined,
      seller_assigned_price: product.minimum_price_including_vat
    });
  }
  emit("onAddProducts", products);
  productToAdd.value = null;
}
function formattedCurrency(currency) {
  return t("currencies." + currency);
}
function onSearch(query) {
  productsQuery.value = query;
  getProducts.value(query);
}
onMounted(async () => {
  getProducts.value = debounce(async query => {
    if (recurringPayments.value) {
      products.value = await downloadProducts(false, ignoreDeleted.value, true, quoteProfileId.value, true, query);
    } else {
      products.value = await downloadProducts(true, ignoreDeleted.value, false, quoteProfileId.value, true, query);
    }
    if (!productsLoaded.value) {
      productsLoaded.value = true;
    }
  }, 250);
  getProducts.value("");
});
watch(ignoreDeleted, () => getProducts.value?.(productsQuery.value));
</script>
